export const HIDDEN = '[скрыто]';
/** Объект глубже предела не пропускаем нечищеным: на его месте — эта метка. */
export const TOO_DEEP = '[скрыто: слишком глубоко]';
const MAX_DEPTH = 12;

/** Имена, которые безобидны, хотя начинаются со слова из списка секретных (`sessions` — список, а не сессия). */
const HARMLESS_SESSION = new Set(['sessions', 'sessionstorage']);

/**
 * `tokens` (список метаданных токенов) и `key` словаря — не секреты.
 * Один файл на виджет и сервер (shared/): виджет при загрузке ставит нейтральные `configureRedact`, сервер живёт на умолчаниях.
 */
export function isSecretKey(key: string): boolean {
  const k = key.toLowerCase().replace(/[-_]/g, '');
  return (
    /password|passwd|secret|apikey|authorization|cookie|accesshash|codehash|apihash|bearer|credential|(?<!micro)phone/.test(k) ||
    k === 'pass' ||
    k === 'code' ||
    k === 'hash' ||
    k === 'jwt' ||
    k.endsWith('jwt') ||
    k === 'otp' ||
    k.endsWith('otp') ||
    k.endsWith('token') ||
    (k.startsWith('session') && !HARMLESS_SESSION.has(k))
  );
}

const SECRET_KEY_TEXT =
  /(password|passwd|secret|api_?key|apiKey|authorization|cookie|access_?hash|api_?hash|bearer|credentials?|\botp\b|\bjwt\b|[a-z_]*token)/;

/** Значения по виду: наши токены, ключи моделей, `Bearer …`, JWT, длинная строка после слова token/secret. */
const SECRET_VALUE_PATTERNS: [RegExp, string][] = [
  [/\b(jbugs|jbproj)_[A-Za-z0-9_-]{16,}|\bsk-[A-Za-z0-9_-]{16,}/g, HIDDEN],
  [/\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, `Bearer ${HIDDEN}`],
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}/g, HIDDEN],
  [/\b((?:token|secret)[A-Za-z_]*["']?\s*[:=]\s*["']?)[A-Za-z0-9_+/=-]{20,}/gi, `$1${HIDDEN}`],
];

/**
 * Персональные данные по виду значения (19.10, «данные маскировались бы, на скриншотах ок, но не в тексте»): почта,
 * телефон, ник `@name`, ссылка на человека в Telegram, длинный числовой номер (Telegram ID, `access_hash`).
 * Имена и тексты сообщений по виду не узнать — их прячут по имени поля (`PII_KEYS`) и по месту в странице (`PII_SELECTORS`).
 */
const PII_VALUE_PATTERNS: [RegExp, string][] = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g, '[почта]'],
  [/\bhttps?:\/\/(?:www\.)?t\.me\/[^\s"'<>)]+/gi, 't.me/[скрыто]'],
  [/\bt\.me\/[A-Za-z0-9_+/-]+/gi, 't.me/[скрыто]'],
  [/(?<![\w@])@[A-Za-z][A-Za-z0-9_]{4,31}\b/g, '@[ник]'],
  [/\+\d[\d\s().-]{8,}\d/g, '[телефон]'],
  [/(?<![\w-])\d[\d\s]{0,2}\(\d{3}\)[\d\s.-]{6,}\d/g, '[телефон]'],
  [/(?<![\w.-])-?\d{17,}(?![\w.-])/g, '[id]'],
  [/(?<![\w.-])\d{9,}(?![\w.-])/g, '[id]'],
];

/**
 * Словарь «известных» имён и текстов: браузер запоминает, что сам получил с сервера (имена людей, слова переписки), и
 * прячет именно это везде — в HTML, атрибутах, заголовках, консоли (`bugReport/known.ts`). У сервера словаря нет.
 */
let knownMask: ((text: string) => string) | null = null;
export function setKnownMask(fn: ((text: string) => string) | null): void {
  knownMask = fn;
}

/** Runs `fn` without the dictionary pass (secrets and contacts are still hidden): its strings get that pass later. */
export function withoutKnownMask<T>(fn: () => T): T {
  const keep = knownMask;
  knownMask = null;
  try {
    return fn();
  } finally {
    knownMask = keep;
  }
}

/** Чистка произвольной строки по виду значения: аргументы console, тексты ошибок, URL, HTML. Секреты и персональные данные. */
export function redactText(text: string): string {
  let out = text;
  for (const pattern of extraSecrets) out = out.replace(pattern, HIDDEN);
  for (const [pattern, replacement] of SECRET_VALUE_PATTERNS) out = out.replace(pattern, replacement);
  for (const [pattern, replacement] of PII_VALUE_PATTERNS) out = out.replace(pattern, replacement);
  return knownMask ? knownMask(out) : out;
}

/**
 * Адреса API, ответы которых — про людей и переписку (кандидаты, чаты, вопросы, портрет, вакансия-сторона…): в их телах
 * прячутся ещё и `name`, `text`, `note`… — любые слова людей, под какими бы полями ни лежали. Критерии и настройки сюда
 * не входят: их тексты нужны для разбора ошибок.
 */
const PERSONAL_URL =
  /\/api\/(?:candidates|recruiter-chat|telegram|companies|matches|sources|facts|theses|search|tasks|vacancies\/[^/?#]+\/(?:side|questions|chat|candidates|portrait|deliveries|checklists|resume|alerts|send|scan|updates))(?![A-Za-z-])/;
/** Host token prefix that predates the service; the defaults below keep the server's behavior as it was in facts. */
const LEGACY_SECRET = /\bcfacts_[A-Za-z0-9_-]{16,}/g;

let personalUrl: RegExp | null = PERSONAL_URL;
let extraSecrets: RegExp[] = [LEGACY_SECRET];

export interface RedactConfig {
  /** API paths whose bodies carry people's words; `null` — none. */
  personalUrl?: RegExp | null;
  /** Extra secret value shapes, hidden whole (built in: `jbugs_`, `jbproj_`, `sk-`, `Bearer …`, JWT). */
  secretPatterns?: RegExp[];
}

const globalOf = (re: RegExp): RegExp => (re.global ? re : new RegExp(re.source, `${re.flags}g`));

/** Only the passed fields change; omitted ones keep their current value. */
export function configureRedact(config: RedactConfig): void {
  if (config.personalUrl !== undefined) personalUrl = config.personalUrl ? new RegExp(config.personalUrl.source, config.personalUrl.flags.replace(/[gy]/g, '')) : null;
  if (config.secretPatterns !== undefined) extraSecrets = config.secretPatterns.map(globalOf);
}

/** Back to the module defaults (facts' personal paths and `cfacts_`). */
export function resetRedactConfig(): void {
  personalUrl = PERSONAL_URL;
  extraSecrets = [LEGACY_SECRET];
}

export function isPersonalUrl(url: string): boolean {
  return personalUrl ? personalUrl.test(url) : false;
}
/** Поля тел «личных» адресов, где лежат слова людей. */
const PERSONAL_URL_KEYS = new Set([
  'name', 'text', 'note', 'notes', 'comment', 'body', 'caption', 'content', 'message', 'answer', 'question', 'reply', 'chattitle',
  'title', 'description', 'label', 'mapping', 'additiontext',
]);
export function isPersonalUrlKey(key: string): boolean {
  return PERSONAL_URL_KEYS.has(key.toLowerCase().replace(/[-_]/g, ''));
}

/** Поля, в которых лежат имена и ники людей: значение прячется целиком, остаётся длина. */
const PII_KEYS = new Set([
  'firstname', 'lastname', 'middlename', 'fullname', 'displayname', 'tgusername', 'username', 'nickname', 'nick',
  'email', 'candidatename', 'contactname', 'ownername', 'authorname', 'sendername', 'peername',
]);
/** Поля с цитатами и пересказами из переписки: слова людей. */
const QUOTE_KEYS = new Set(['quote', 'quotes', 'evidence', 'contextbefore', 'contextafter', 'snippet', 'excerpt', 'summary']);
/** Признаки сообщения Telegram: объект с `text` и одним из них — тело переписки. */
const MESSAGE_MARKS = ['replyToId', 'fwdFrom', 'out', 'peerId', 'senderId', 'attachment'];

/** Метка вместо спрятанного текста: сколько там было — видно, что там — нет. */
export function hiddenText(text: string): string {
  return `[скрыто: ${text.length} симв.]`;
}

function isPiiKey(key: string): boolean {
  return PII_KEYS.has(key.toLowerCase().replace(/[-_]/g, ''));
}
function isQuoteKey(key: string): boolean {
  return QUOTE_KEYS.has(key.toLowerCase().replace(/[-_]/g, ''));
}

/** `?a=1&code=xyz#access_token=…`: значения секретных параметров — скрыты, остальное чистится по виду значения. */
export function redactQuery(text: string): string {
  return redactText(
    text.replace(/([?&#;]?)([^=&#;\s?]+)=([^&#;\s]*)/g, (match, lead: string, name: string, value: string) => {
      let decoded = name;
      try {
        decoded = decodeURIComponent(name);
      } catch {
        // имя не раскодировалось — проверяем как есть
      }
      return isSecretKey(decoded) && value !== '' ? `${lead}${name}=${HIDDEN}` : match;
    }),
  );
}

function redactValue(value: unknown, depth: number, personal: boolean): unknown {
  if (typeof value === 'string') return redactText(value);
  if (!value || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return TOO_DEEP;
  if (Array.isArray(value)) return value.map((item) => redactValue(item, depth + 1, personal));
  const record = value as Record<string, unknown>;
  const isMessage = typeof record.text === 'string' && MESSAGE_MARKS.some((mark) => mark in record);
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(record)) {
    if (isSecretKey(key) && item !== null && item !== '') out[key] = HIDDEN;
    else if (
      typeof item === 'string' &&
      item !== '' &&
      (isPiiKey(key) || isQuoteKey(key) || (isMessage && key === 'text') || (personal && isPersonalUrlKey(key)))
    ) {
      out[key] = hiddenText(item);
    } else out[key] = redactValue(item, depth + 1, personal);
  }
  return out;
}

export function redact<T>(value: T, options: { personal?: boolean } = {}): T {
  return redactValue(value, 0, options.personal === true) as T;
}

/**
 * Bodies cut at the read limit are not valid JSON and fall back to the text pattern. `url` — адрес запроса: у «личных»
 * адресов (`isPersonalUrl`) прячутся и имена, и тексты — в обрезанном теле тоже, по шаблону `"поле":"значение"`.
 */
export function redactBody(text: string, max: number, url = ''): string {
  const personal = isPersonalUrl(url);
  let clean: string;
  try {
    clean = JSON.stringify(redact(JSON.parse(text), { personal }));
  } catch {
    clean = redactText(
      text
        .replace(new RegExp(`("?${SECRET_KEY_TEXT.source}"?\\s*[:=]\\s*)("[^"]*"|[^&\\s,}]+)`, 'gi'), `$1${HIDDEN}`)
        .replace(/("([A-Za-z_-]{1,40})"\s*:\s*)"((?:[^"\\]|\\.)*)"/g, (match, lead: string, key: string, value: string) =>
          value !== '' && (isPiiKey(key) || isQuoteKey(key) || (personal && isPersonalUrlKey(key))) ? `${lead}"${hiddenText(value)}"` : match,
        ),
    );
  }
  return clean.length > max ? `${clean.slice(0, max)}… [ещё ${clean.length - max} симв.]` : clean;
}

export function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}… [ещё ${text.length - max} симв.]` : text;
}
